import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, X, Loader2, Calendar, Clock, User, Phone } from "lucide-react";
import { Add, DocumentUpload } from "iconsax-reactjs";
import AskGenziButton from "./AskGenziButton";
import api from "../../services/api";

const UploadCall = ({ onSuccess, onCancel, isModal = false }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    extensionNumber: "",
    callerId: "",
    callerPhone: "",
    direction: "inbound",
    callStartTime: "",
    callEndTime: "",
    platform: "manual_upload",
    metadata: {
      notes: "",
    },
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file is an audio file
    if (!file.type.startsWith("audio/")) {
      alert("Please select an audio file");
      return;
    }

    // Check file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      alert("File size must be less than 100MB");
      return;
    }

    setSelectedFile(file);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("metadata.")) {
      const metadataField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [metadataField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      alert("Please select an audio file to upload");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("audio", selectedFile);

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (key !== "metadata") {
          uploadFormData.append(key, formData[key]);
        }
      });

      // Append metadata as JSON string
      uploadFormData.append("metadata", JSON.stringify(formData.metadata));

      // Simulate progress (in a real app, you'd use axios progress events)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 300);

      const response = await api.uploadCallRecording(uploadFormData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success) {
        // Wait a moment to show completion
        setTimeout(() => {
          if (isModal && onSuccess) {
            // If in modal mode, call the success callback
            onSuccess("Call uploaded successfully! Processing has started.");
          } else {
            // If in page mode, navigate normally
            navigate("/transcripts", {
              state: {
                message: "Call uploaded successfully! Processing has started.",
                callId: response.callRecording.id,
              },
            });
          }
        }, 500);
      } else {
        throw new Error(response.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert(`Upload failed: ${error.message || "Unknown error"}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleCancel = () => {
    if (isModal && onCancel) {
      onCancel();
    } else {
      navigate("/transcripts");
    }
  };

  // If this is a modal, we don't need the full page wrapper
  const content = (
    <div className={isModal ? "" : "min-h-screen bg-[#0C0E12] text-white"}>
      <div className={isModal ? "" : "p-4 md:p-8 mx-auto max-w-4xl"}>
        {/* Header - Only show if not in modal */}
        {!isModal && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
                Upload Call Recording
              </h1>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                Upload audio files for transcription and analysis.
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
              <button
                onClick={handleCancel}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0"
              >
                <X size={16} className="shrink-0" />
                <span className="whitespace-nowrap">Cancel</span>
              </button>
            </div>
          </div>
        )}

        {/* Upload Form */}
        <div className="bg-[#13161B] border border-[#22262F]  p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Upload Section */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Audio File
              </label>

              {!selectedFile ? (
                <div
                  className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-gray-500 transition-colors"
                  onClick={triggerFileInput}
                >
                  <DocumentUpload
                    size={48}
                    className="mx-auto text-gray-500 mb-4"
                  />
                  <p className="text-gray-400 mb-2">
                    Click to select or drag and drop
                  </p>
                  <p className="text-sm text-gray-500">
                    Supports MP3, WAV, M4A, and other audio formats (max 100MB)
                  </p>
                </div>
              ) : (
                <div className="border border-gray-700 rounded-lg p-4 bg-[#0C0E12]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <DocumentUpload size={20} className="text-blue-400" />
                      <span className="text-white truncate max-w-xs">
                        {selectedFile.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="text-gray-400 hover:text-white p-1"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="text-sm text-gray-400">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />
            </div>

            {/* Progress Bar */}
            {isUploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-400">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Call Details Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <User size={16} className="inline mr-2" />
                  Caller ID
                </label>
                <input
                  type="text"
                  name="callerId"
                  value={formData.callerId}
                  onChange={handleInputChange}
                  className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                  placeholder="Caller name or identifier"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Phone size={16} className="inline mr-2" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="callerPhone"
                  value={formData.callerPhone}
                  onChange={handleInputChange}
                  className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                  placeholder="Phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Extension Number
                </label>
                <input
                  type="text"
                  name="extensionNumber"
                  value={formData.extensionNumber}
                  onChange={handleInputChange}
                  className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                  placeholder="Extension number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Direction
                </label>
                <select
                  name="direction"
                  value={formData.direction}
                  onChange={handleInputChange}
                  className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="inbound">Inbound</option>
                  <option value="outbound">Outbound</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Calendar size={16} className="inline mr-2" />
                  Call Start Time
                </label>
                <input
                  type="datetime-local"
                  name="callStartTime"
                  value={formData.callStartTime}
                  onChange={handleInputChange}
                  className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Clock size={16} className="inline mr-2" />
                  Call End Time
                </label>
                <input
                  type="datetime-local"
                  name="callEndTime"
                  value={formData.callEndTime}
                  onChange={handleInputChange}
                  className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Notes
              </label>
              <textarea
                name="metadata.notes"
                value={formData.metadata.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                placeholder="Add any notes about this call..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              {isModal && (
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isUploading}
                  className="flex-1 px-6 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={isUploading || !selectedFile}
                className={`${
                  isModal ? "flex-1" : "w-full"
                } flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed`}
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                  border: "1px solid",
                  borderImageSource:
                    "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                  boxShadow: `
                    0px 36px 32px -16px #7F56D936,
                    0px 8px 16px 0px #7F56D91F,
                    0px 2px 4px -8px #7F56D914,
                    0px 8px 24px -16px #FFFFFF3D inset,
                    0px -24px 32px 0px #FFFFFF38 inset,
                    0px 0px 12px 0px #FFFFFF inset
                  `,
                  borderImageSlice: "1",
                }}
              >
                {isUploading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={20} />
                    Upload Call Recording
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Only show AskGenziButton if not in modal */}
      {!isModal && <AskGenziButton />}
    </div>
  );

  return content;
};

export default UploadCall;
